import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ConsentService } from './consent.service';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}

export interface MetaViewContentParams {
  content_ids?: string[];
  content_type?: string;
}

export interface MetaAddToCartParams {
  value: number;
  currency: string;
  content_ids?: string[];
}

/**
 * Gets a cookie value by name.
 */
function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : undefined;
}

/**
 * Generates a UUID v4 for event deduplication between Pixel and CAPI.
 */
function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

const FB_EVENTS_URL = 'https://connect.facebook.net/en_US/fbevents.js';

interface FbqStub {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  push: FbqStub;
  loaded: boolean;
  version: string;
  queue: unknown[][];
}

/**
 * Creates the fbq stub that Meta Pixel expects before the script loads.
 * The stub queues calls until fbevents.js replaces it with the real implementation.
 */
function ensureFbqStub(): void {
  if (typeof window === 'undefined') return;
  if ((window as { fbq?: unknown }).fbq) return;
  const n = (function (this: unknown) {
    const stub = n as FbqStub;
    const args = Array.from(arguments);
    stub.callMethod ? stub.callMethod.apply(stub, args) : stub.queue.push(args);
  }) as FbqStub;
  (window as unknown as { fbq: FbqStub }).fbq = n;
  (window as unknown as { _fbq: FbqStub })._fbq = n;
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
}

@Injectable({
  providedIn: 'root'
})
export class MetaPixelService {
  private inited = false;
  private scriptLoaded = false;
  private scriptLoadPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private consentService: ConsentService
  ) {
    this.consentService.consentStatus$.subscribe((status) => {
      if (status === 'accepted') {
        this.ensureInit();
      }
    });
  }

  /**
   * Load Meta Pixel script dynamically. Only loads when consent is granted.
   * Creates fbq stub first so the script finds it when it executes.
   */
  private loadScript(): Promise<void> {
    if (this.scriptLoaded) return Promise.resolve();
    if (this.scriptLoadPromise) return this.scriptLoadPromise;
    ensureFbqStub();
    this.scriptLoadPromise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = FB_EVENTS_URL;
      script.async = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      document.head.appendChild(script);
    });
    return this.scriptLoadPromise;
  }

  /**
   * Initialize the Pixel and send PageView. Called when consent is granted.
   */
  private ensureInit(): void {
    const pixelId = environment.meta?.pixelId;
    if (!pixelId || this.inited) {
      return;
    }
    if (this.consentService.hasConsent() !== true) {
      return;
    }
    this.loadScript().then(() => {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('init', pixelId);
        window.fbq('track', 'PageView');
        this.inited = true;
      }
    });
  }

  /**
   * Track ViewContent (product/shop view). Sends to Pixel and optionally to CAPI with same event_id.
   * Only tracks when user has accepted cookies.
   */
  trackViewContent(params?: MetaViewContentParams): string | null {
    if (this.consentService.hasConsent() !== true) return null;
    if (!environment.meta?.pixelId) return null;

    const eventId = generateEventId();
    this.ensureInit();
    this.loadScript().then(() => {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        const payload: Record<string, unknown> = {};
        if (params?.content_ids?.length) {
          payload['content_ids'] = params.content_ids;
        }
        if (params?.content_type) {
          payload['content_type'] = params.content_type;
        }
        window.fbq('track', 'ViewContent', payload, { eventID: eventId });
      }
      this.sendToCAPI('ViewContent', eventId, {
        content_ids: params?.content_ids,
        content_type: params?.content_type ?? 'product'
      });
    });
    return eventId;
  }

  /**
   * Track AddToCart. Sends to Pixel and optionally to CAPI with same event_id.
   * Only tracks when user has accepted cookies.
   */
  trackAddToCart(value: number, currency: string, contentIds?: string[]): string | null {
    if (this.consentService.hasConsent() !== true) return null;
    if (!environment.meta?.pixelId) return null;

    const eventId = generateEventId();
    this.ensureInit();
    this.loadScript().then(() => {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        const payload: Record<string, unknown> = { value, currency };
        if (contentIds?.length) {
          payload['content_ids'] = contentIds;
        }
        window.fbq('track', 'AddToCart', payload, { eventID: eventId });
      }
      this.sendToCAPI('AddToCart', eventId, {
        value,
        currency,
        content_ids: contentIds
      });
    });
    return eventId;
  }

  /**
   * Forward event to CAPI proxy (backend) for deduplication and prioritization.
   */
  private sendToCAPI(
    eventName: string,
    eventId: string,
    customData: Record<string, unknown>
  ): void {
    const url = environment.meta?.capiEndpointUrl;
    if (!url) {
      return;
    }
    const payload: Record<string, unknown> = {
      event_name: eventName,
      event_id: eventId,
      ...customData
    };
    const fbp = getCookie('_fbp');
    const fbc = getCookie('_fbc');
    if (fbp) payload['fbp'] = fbp;
    if (fbc) payload['fbc'] = fbc;
    this.http
      .post(url, payload, { withCredentials: false })
      .subscribe({
        error: (err) => {
          console.warn('[Meta CAPI] Failed to send event:', eventName, err);
        }
      });
  }
}
